import React, { useState, useRef } from "react";
import { Drawer, Form, Input, Icon, notification, Modal } from "antd";
import axios from "axios";
import moment from "moment";

import EditorHtml from "../../../shared/components/Form/EditorHtml";

import { cleanHtmlContent } from "../../../shared/utils/Diario/htmlCleanner";

const { TextArea } = Input;
const openNotificationWithIcon = (type, message, description) => {
    notification[type]({
        message: message,
        description: description
    });
};

const DrawerAdicionarParecerAnual = ({ disciplinaId, parecerInfo, visible, onClose, onConfirm, onRascunhoGuardado, form }) => {
    const { getFieldDecorator } = form;

    const [participante, setParticipante] = useState(null);
    const [parecer, setParecer] = useState("");
    const [iconLoading, setIconLoading] = useState(false);
    const [editorVisible, setEditorVisible] = useState(false);

    const editorRef = useRef(null);
    const debounceRef = useRef(null);
    const parecerInicialRef = useRef("");
    const teclaPremiodaRef = useRef(false);
    const rascunhoGuardadoRef = useRef(false);

    const normalizeHtml = html => {
        if (!html) return "";
        return html.replace(/\s+/g, " ").trim();
    };

    const handleEditorChange = conteudo => {
        if (!teclaPremiodaRef.current) return;
        if (!visible) return;
        if (normalizeHtml(conteudo) === normalizeHtml(parecerInicialRef.current)) return;

        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        debounceRef.current = setTimeout(() => {
            guardarRascunho();
        }, 500);
    };

    const guardar = () => {
        form.validateFieldsAndScroll((err, values) => {
            if (!err) {
                setIconLoading(true);

                const cleanContent = cleanHtmlContent(editorRef.current.value);
                const utilizadorId = participante?.id;

                var item = new FormData();
                item.append("turmaId", disciplinaId);
                item.append("utilizadorId", utilizadorId);
                item.append("parecer", cleanContent);

                axios({
                    method: "put",
                    url: "/api/pareceres/guardar-parecer-anual",
                    data: item
                })
                    .then(() => {
                        openNotificationWithIcon("success", "Sucesso", "Parecer adicionado!");
                        onConfirm(cleanContent);
                        parecerInicialRef.current = "";
                        setIconLoading(false);
                    })
                    .catch(() => {
                        openNotificationWithIcon("error", "Erro", "Não foi possível guardar o parecer!");
                        setIconLoading(false);
                    });
            }
        });
    };

    const guardarRascunho = () => {
        const utilizadorId = participante?.id;
        const cleanContent = cleanHtmlContent(editorRef.current.value);

        var item = new FormData();
        item.append("turmaId", disciplinaId);
        item.append("utilizadorId", utilizadorId);
        item.append("parecer", cleanContent);

        axios({
            method: "put",
            url: "/api/pareceres/guardar-parecer-anual-rascunho",
            data: item
        })
            .then(() => {
                rascunhoGuardadoRef.current = true;
                if (onRascunhoGuardado && utilizadorId) {
                    onRascunhoGuardado(utilizadorId, cleanContent, new Date().toISOString());
                }
            })
            .catch(() => {
                openNotificationWithIcon("error", "Erro", "Não foi possível guardar o rascunho!");
            });
    };

    const afterVisibleChange = aberto => {
        if (aberto) {
            form.resetFields();
            setEditorVisible(true);

            const parecerOriginal = parecerInfo.participante.parecer_anual;
            const parecerRascunho = parecerInfo.participante.parecer_anual_rascunho;
            const dtRascunho = parecerInfo.participante.dt_parecer_anual_rascunho;

            setParticipante(parecerInfo.participante);

            setParecer(parecerOriginal);
            parecerInicialRef.current = parecerOriginal;

            if (parecerRascunho && normalizeHtml(parecerRascunho) !== normalizeHtml(parecerOriginal)) {
                const content = dtRascunho
                    ? `Existe um rascunho guardado para este parecer a ${moment(dtRascunho).format("DD/MM/YYYY [às] HH:mm")}. Deseja carregar os dados do rascunho?`
                    : "Existe um rascunho guardado para este parecer. Deseja carregar os dados do rascunho?";

                Modal.confirm({
                    title: "Rascunho encontrado",
                    content: content,
                    okText: "Sim, carregar",
                    cancelText: "Não",
                    onOk: () => {
                        setParecer(parecerRascunho);
                        parecerInicialRef.current = parecerRascunho;
                    }
                });
            }
        } else {
            setEditorVisible(false);
            parecerInicialRef.current = "";
            teclaPremiodaRef.current = false;
        }
    };

    return (
        <>
            <Drawer
                title="Preencher o parecer anual"
                className="drawer-from-pagina-html"
                width={720}
                onClose={onClose}
                visible={visible}
                style={{
                    overflow: "auto",
                    height: "calc(100% - 108px)",
                    paddingBottom: "108px"
                }}
                maskClosable={false}
                afterVisibleChange={afterVisibleChange}
            >
                <div>
                    <div
                        className="bloco"
                        onKeyDown={() => {
                            teclaPremiodaRef.current = true;
                        }}
                        onPaste={() => {
                            teclaPremiodaRef.current = true;
                        }}
                    >
                        {editorVisible && (
                            <EditorHtml
                                onChange={handleEditorChange}
                                value={parecer}
                                disabledPlugins="addnewline,cleanhtml,clipboard,draganddrop,draganddropelement,paste,pastestorage,errormessages,formatblock,imageprocessor,imageproperties,inlinepopup,limit,media,tablekeyboardnavigation,tooltip"
                                activeButtonsInReadOnly={["source", "fullsize", "print", "about"]}
                                buttons="bold,strikethrough,underline,italic,eraser,|,ul,ol,|,fontsize,brush,paragraph,|,align,undo,redo,|,fullsize"
                                editorRef={editorRef}
                            />
                        )}
                    </div>
                </div>
                <div className="ant-drawer-footer">
                    <button
                        className="botao-secundario"
                        onClick={() => {
                            if (rascunhoGuardadoRef.current)
                                openNotificationWithIcon("success", "Rascunho guardado", "Os dados foram salvos como rascunho.");
                            rascunhoGuardadoRef.current = false;
                            onClose();
                        }}
                        style={{ marginRight: 20, display: "inline-block" }}
                    >
                        Voltar
                    </button>
                    <button className="botao-principal" onClick={guardar} disabled={iconLoading} type="primary">
                        {iconLoading ? <Icon type="loading" /> : null}
                        Salvar
                    </button>
                </div>
            </Drawer>
        </>
    );
};

const FormDrawerAdicionarParecerAnual = Form.create({ name: "form-drawer-adicionar-parecer-anual" })(DrawerAdicionarParecerAnual);

export default FormDrawerAdicionarParecerAnual;
